/* =============================================================================
 * Bootstrap da sessao.
 *
 * As unicas chamadas de API que NAO moram numa pagina, por dois motivos:
 *
 *  1. Quem chama sao dois lados - o boot em app/main.js e a tela de login.
 *     app/ nao pode importar de pages/, entao o codigo nao cabe na tela.
 *  2. Elas alimentam a sessao e as capacidades, nao uma tela.
 *
 * Fica separado de sessao.js por causa do ciclo de import: sessao.js nao pode
 * conhecer o ServicoAplicacao, que le o token dela.
 * ========================================================================== */

import { executarFuncionalidade } from '@shared/api/ServicoAplicacao.js';
import { lerImagemComoFileBytes } from '@shared/lib/arquivos.js';
import { paraInteiro, paraLista, paraTexto } from '@shared/lib/valores.js';
import { sessao, aplicarShell, definirClubesVinculados, definirAtletasVinculados, definirImagemUsuario } from './sessao.js';

/**
 * Boot anonimo. Alem das informacoes de navegacao, e' daqui que sai o
 * IdentificadorConexao (o tenant) - sem ele nenhuma URL de imagem funciona.
 */
export function obterShellAnonimo(opcoes = {}) {
    return executarFuncionalidade({ NomeFuncionalidade: 'ObterInformacoesShellUsuarioAnonimo' }, { semToken: true, ...opcoes });
}

export function obterShellUsuario(idUsuario, idPlataforma = 0, opcoes = {}) {
    return executarFuncionalidade(
        {
            NomeFuncionalidade: 'ObterInformacoesShellUsuario',
            IdUsuario: paraInteiro(idUsuario),
            IdPlataforma: paraInteiro(idPlataforma),
        },
        opcoes
    );
}

/**
 * Troca a foto do usuario logado.
 *
 * MORA AQUI, e nao na tela, porque o resultado dela e' a SESSAO: o retrato
 * aparece na barra lateral, no cabecalho, na nav inferior e no perfil, e todos
 * eles se repintam pelo evento que `definirImagemUsuario` emite. A tela so'
 * escolhe o arquivo e mostra o erro.
 *
 * `AlterarTabUsuarioFoto` grava em TAB_USUARIO_FOTO e copia o caminho para
 * TAB_USUARIO.IMAGEM. Ela nao tem propriedade de saida: o caminho novo volta no
 * PROPRIO FileBytes que subiu, porque o CriarTabArquivo escreve `FullName` nele
 * depois de salvar o arquivo (e zera o base64 na volta - a resposta e' pequena).
 *
 * A releitura do shell e' rede de seguranca, nao o caminho normal: se um dia a
 * funcionalidade parar de devolver o FileBytes, a foto ainda aparece - custando
 * uma chamada a mais em vez de falhar em silencio, que e' o pior desfecho aqui
 * (a pessoa escolhe a foto, nada acontece e nao ha' erro para explicar).
 */
export async function trocarMinhaFoto(arquivo, opcoes = {}) {
    const idUsuario = paraInteiro(sessao.usuario?.IdUsuario);
    if (idUsuario <= 0) throw new Error('É preciso estar conectado para trocar a foto.');

    const fileBytes = await lerImagemComoFileBytes(arquivo);
    const r = await executarFuncionalidade({ NomeFuncionalidade: 'AlterarTabUsuarioFoto', IdUsuario: idUsuario, FileBytes: fileBytes }, opcoes);

    const caminho = paraTexto(r?.FileBytes?.FullName);
    if (caminho) {
        definirImagemUsuario(caminho);
        return caminho;
    }

    aplicarShell(await obterShellUsuario(idUsuario, 0, opcoes));
    //`aplicarShell` nao grava o usuario no aparelho - so' o repoe em memoria.
    //O retrato tem que sobreviver ao recarregamento tanto quanto no caminho de
    //cima, entao a gravacao passa pelo mesmo lugar.
    definirImagemUsuario(sessao.usuario?.Imagem);
    return paraTexto(sessao.usuario?.Imagem);
}

/**
 * Clubes vinculados ao usuario.
 *
 * O vinculo NAO esta' em TAB_USUARIO_PERFIL - essa tabela so' diz o TIPO do
 * perfil ('Clube'), nunca de qual clube. Quem amarra e' TAB_USUARIO_CLUBE, que
 * ainda traz PermiteAdministrar (dirigente x apenas vinculado).
 *
 * Usa a funcionalidade ObterTabUsuarioClubes, criada para isto. A alternativa
 * seria /listarnegocios, que foi descartada por dois motivos: os criterios sao
 * SQL cru vindo do cliente, e naquele endpoint a chamada retorna HTTP 500.
 */
async function obterMeusClubes(idUsuario, opcoes = {}) {
    const id = paraInteiro(idUsuario);
    if (id <= 0) return [];

    const r = await executarFuncionalidade({ NomeFuncionalidade: 'ObterTabUsuarioClubes', IdUsuario: id }, opcoes);

    return paraLista(r.Clubes).map((v) => ({
        idClube: paraInteiro(v.IdClube),
        clube: v.Clube ?? '',
        permiteAdministrar: v.PermiteAdministrar === true,
    }));
}

async function obterMeusAtletas(idUsuario, opcoes = {}) {
    const r = await executarFuncionalidade({ NomeFuncionalidade: 'ObterTabUsuarioAtletas', IdUsuario: paraInteiro(idUsuario) }, opcoes);
    return paraLista(r.Atletas);
}

/**
 * Carrega os vinculos que definem as capacidades (clubes que administra,
 * atletas que representa). Roda depois do shell, no boot e apos o login.
 *
 * Falha aqui NAO derruba a sessao: a pessoa continua navegando como torcedora,
 * so' nao ve os atalhos de gestao. Por isso cada parte e' tolerada em separado.
 */
export async function carregarVinculosUsuario(idUsuario) {
    const id = paraInteiro(idUsuario);
    if (id <= 0) return;

    const [clubes, atletas] = await Promise.allSettled([obterMeusClubes(id), obterMeusAtletas(id)]);

    if (clubes.status === 'fulfilled') definirClubesVinculados(clubes.value);
    else console.warn('[sessao] não foi possível carregar os clubes do usuário', clubes.reason);

    if (atletas.status === 'fulfilled') definirAtletasVinculados(atletas.value);
    else console.warn('[sessao] não foi possível carregar os atletas do usuário', atletas.reason);
}
